import { useState } from 'react';
import { T, F } from '@/lib/dispatchApi';
import BricksLoader from '@/components/BricksLoader';

/**
 * The one button for the photographer (dispatch) pages.
 * 48px tall, radius 2, one-line labels. main: Bricks orange, the one main action on a screen ·
 * primary: solid off-white · secondary: 1px graphite
 * outline · tertiary: text only · danger: red outline. Pressed + disabled states built in.
 * Pass `href` to render a link (e.g. tel:) with the same look.
 */
const GRAPHITE = T.graphite;
const GRAPHITE_PRESS = T.graphitePress;
const LOOKS = {
  // Ink on brick is 4.52:1 (AA); white on brick is only 4.16:1. Pressed lightens so ink stays readable.
  main: { bg: T.brick, border: T.brick, fg: '#14110E', pressBg: '#DD6A43', pressBorder: '#DD6A43' },
  primary: { bg: T.moon, border: T.moon, fg: T.night, pressBg: T.moonPress, pressBorder: T.moonPress },
  secondary: { bg: 'transparent', border: GRAPHITE, fg: T.moon, pressBg: T.nightSoft, pressBorder: GRAPHITE_PRESS },
  tertiary: { bg: 'transparent', border: 'transparent', fg: T.moonSoft, pressBg: T.nightSoft, pressBorder: 'transparent' },
  danger: { bg: 'transparent', border: T.badText, fg: T.badText, pressBg: T.bad + '1F', pressBorder: T.badText },
};

export default function DispatchButton({
  variant = 'secondary', icon: Icon, children, onClick, disabled, loading, href, full, style, ...rest
}) {
  const [pressed, setPressed] = useState(false);
  const look = LOOKS[variant] || LOOKS.secondary;
  const inactive = disabled || loading;
  const off = disabled && !loading;
  // Disabled: a quiet filled block with a hairline edge and mute (AA) text in both themes.
  const fg = off ? T.moonMute : look.fg;
  const box = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    height: 48, minWidth: 44, padding: '0 16px', boxSizing: 'border-box', borderRadius: 2,
    border: `1px solid ${off ? (variant === 'primary' || variant === 'main' ? T.nightHairS : variant === 'tertiary' ? 'transparent' : T.nightHair) : pressed ? look.pressBorder : look.border}`,
    backgroundColor: off ? (variant === 'primary' || variant === 'main' ? T.nightDeep : 'transparent') : pressed ? look.pressBg : look.bg,
    color: fg, fontFamily: F.heading, fontSize: 14, fontWeight: 500, letterSpacing: 0,
    whiteSpace: 'nowrap', textDecoration: 'none', cursor: inactive ? 'not-allowed' : 'pointer',
    width: full ? '100%' : undefined, transition: 'background-color 0.1s, border-color 0.1s',
    ...style,
  };
  const inner = loading
    ? <BricksLoader variant="cycle" size={18} color={look.fg} />
    : <>{Icon ? <Icon size={16} color={fg} strokeWidth={1.5} /> : null}<span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</span></>;
  const handlers = {
    onPointerDown: () => !inactive && setPressed(true),
    onPointerUp: () => setPressed(false),
    onPointerLeave: () => setPressed(false),
  };
  if (href) {
    return <a href={href} style={box} {...handlers} {...rest}>{inner}</a>;
  }
  return (
    <button type="button" className="bx-tap" onClick={onClick} disabled={inactive} aria-busy={!!loading} style={box} {...handlers} {...rest}>
      {inner}
    </button>
  );
}
